import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import {
  AppHeader,
  AppHeaderBackButton,
  AppHeaderLinearProgress,
  Button,
  ButtonGroup,
  Container,
  Form,
  MenuItem,
  Radio,
  RadioGroup,
  RadioTypography,
  Select,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { GiftMemberTypes, GiftMessage, Pages } from '@lib/types';
import { getNextPage } from '../utils/getNextPage';
import { getMessageList, getProgressProps } from '../utils/helpers';
import { CurrentPayPlan, FlowType } from '@ace-clublabs/common-membership-api-types';
import { UtagType } from '../utils/UtagHelper';
import { AppProgressHeader } from '../components/AppProgressHeader';

export const SendMembershipCardPage: React.FC = () => {
  const history = useHistory();

  const { membershipInfo, refreshSession, loadingInfo, sessionData, sendTag, updateMembershipForm, updateBillPlan } =
    useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const { giftDetails } = formData;

  const getInitialSendMembershipCardState = () => {
    return {
      membershipCardRecipient: giftDetails?.membershipCardRecipient || undefined,
      messageCode: giftDetails?.messageCode || undefined,
      giftBillRecipientSelected: giftDetails?.giftBillRecipientSelected || false,
    };
  };

  const [sendMembershipCardForm, setSendMembershipCardForm] = useState(getInitialSendMembershipCardState());

  const [isTagging, setIsTagging] = useState(true);
  const [messageList, setMessageList] = useState<any[]>([]);

  // Disable next button if any field is empty
  const [firstRadioValue, setFirstRadioValue] = useState<string | undefined>(giftDetails?.membershipCardRecipient);
  const [selectValue, setSelectValue] = useState<string | undefined>(giftDetails?.messageCode);
  const [secondRadioValue, setSecondRadioValue] = useState<string | undefined>(formData.billPlan);
  const isSubmitDisabled = !firstRadioValue || !secondRadioValue || !selectValue;

  const progressProps = getProgressProps(sessionData.flowType!, Pages.SendMembershipCardPage);

  const tag = () => {
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:mailing' : 'select level';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  };

  useEffect(() => {
    if (membershipInfo) {
      const finalMessageList = getMessageList(membershipInfo);
      setMessageList(finalMessageList);
    }
  }, [membershipInfo]);

  useEffect(() => {
    const updatedFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        membershipCardRecipient: sendMembershipCardForm.membershipCardRecipient,
        messageCode: sendMembershipCardForm.messageCode,
        giftBillRecipientSelected: sendMembershipCardForm.giftBillRecipientSelected,
      },
    };
    updateMembershipForm(updatedFormData);
  }, [sendMembershipCardForm]);

  const submitSendMembershipCardToOnClick = async () => {
    history.push(getNextPage(Pages.SendMembershipCardPage, sessionData.flowType));
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo) {
      return;
    }
    //update!!!!!!
    //sendTag(UtagType.view, 'sendgiftcard');
    tag();
    setIsTagging(false);
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <Container maxWidth="xs" className="send-gift-to-view">
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      <Form
        onSubmit={submitSendMembershipCardToOnClick}
        sx={{
          '& .MuiRadioGroup-FormControl': {
            maxWidth: '100% !important',
          },
        }}
      >
        <Stack>
          <Typography className="send-membership-card-title" variant="h5Medium" sx={{ marginBottom: '0.5rem' }}>
            Where should we send the membership cards?
          </Typography>
          <RadioGroup
            className="select-where-to-send-mem-card"
            label="Select one"
            sx={{ gap: '0.5rem' }}
            onChange={(v) => setFirstRadioValue(v.target.value)}
            value={
              sendMembershipCardForm.membershipCardRecipient === GiftMemberTypes.RECIPIENT
                ? 'giftRecipient'
                : sendMembershipCardForm.membershipCardRecipient === GiftMemberTypes.DONOR
                ? 'giftDonor'
                : null
            }
          >
            <Radio
              label={<RadioTypography>To the gift recipient</RadioTypography>}
              value="giftRecipient"
              id="sendCardToGiftRecipient"
              onClick={() => {
                setSendMembershipCardForm((prev) => ({ ...prev, membershipCardRecipient: GiftMemberTypes.RECIPIENT }));
              }}
            />
            <Radio
              label={<RadioTypography>To me</RadioTypography>}
              value="giftDonor"
              id="sendCardToDonor"
              onClick={() => {
                setSendMembershipCardForm((prev) => ({ ...prev, membershipCardRecipient: GiftMemberTypes.DONOR }));
              }}
            />
          </RadioGroup>
        </Stack>
        <Stack sx={{ marginTop: '1rem' }}>
          <Select
            label="Greeting messages"
            id="giftMessageDropdown"
            fullWidth={true}
            value={messageList.find((msg) => msg.code === sendMembershipCardForm.messageCode)?.message || ''}
            placeholder="Select one"
            displayEmpty
            onChange={(v) => {
              setSelectValue(v.target.value);
              setSendMembershipCardForm((prev) => ({ ...prev, messageCode: v.target.value }));
            }}
          >
            {messageList.map((message: GiftMessage, i: number) => (
              <MenuItem key={i} value={message.code}>
                {message.message}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack marginTop="2rem">
          <Typography className="send-membership-bill-title" variant="h5Medium" sx={{ marginBottom: '0.5rem' }}>
            Where should we send the annual renewal bill?
          </Typography>
          <RadioGroup
            className="select-where-to-send-bill"
            label="Select one"
            sx={{ gap: '0.5rem' }}
            onChange={(v) => setSecondRadioValue(v.target.value)}
            value={
              giftDetails?.giftBillRecipientSelected === true
                ? formData.billPlan === CurrentPayPlan.ONETIME
                  ? 'giftRecipient'
                  : formData.billPlan === CurrentPayPlan.RECURRINGDONOR
                  ? 'giftDonor'
                  : null
                : null
            }
          >
            <Radio
              label={<RadioTypography>To the gift recipient</RadioTypography>}
              value="giftRecipient"
              id="sendToGiftRecipient"
              onClick={() => {
                setSendMembershipCardForm((prev) => ({ ...prev, giftBillRecipientSelected: true }));
                updateBillPlan(CurrentPayPlan.ONETIME);
              }}
            />
            <Radio
              label={<RadioTypography>To me</RadioTypography>}
              value="giftDonor"
              id="sendToDonor"
              onClick={() => {
                setSendMembershipCardForm((prev) => ({ ...prev, giftBillRecipientSelected: true }));
                updateBillPlan(CurrentPayPlan.RECURRINGDONOR);
              }}
            />
          </RadioGroup>
        </Stack>

        <ButtonGroup marginTop={'1.5rem'}>
          <Button id="nextButton" type="submit" className="btn-large" label="Next" disabled={isSubmitDisabled} />
        </ButtonGroup>
      </Form>
    </Container>
  );
};
